import { KeyboardArrowDown } from "@mui/icons-material";
import type { SelectChangeEvent } from "@mui/material";
import {
  Box,
  MenuItem,
  Select,
  Skeleton,
  Typography,
  useTheme,
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import type { ReactElement, ReactNode } from "react";

const getFigmaFieldLabelSx = (theme: Theme) => ({
  fontFamily: theme.typography.fontFamily,
  fontWeight: 400,
  fontSize: 12,
  lineHeight: "16px",
  letterSpacing: "0.02em",
  color: theme.colors.text[2],
});

const getFigmaRoundedSelectSx = (
  theme: Theme,
  width: number | string,
  fontSize: number | string,
) => ({
  width,
  height: 40,
  backgroundColor: theme.colors.grey,
  borderRadius: "16px",
  "&.MuiOutlinedInput-root": {
    borderRadius: "16px",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "& .MuiSelect-select.MuiOutlinedInput-input": {
    display: "block",
    boxSizing: "border-box",
    fontFamily: theme.typography.fontFamily,
    fontWeight: 500,
    fontSize,
    lineHeight: "20px",
    letterSpacing: "0.02em",
    color: theme.colors.text[6],
    minHeight: 20,
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "18px",
    paddingRight: "44px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  "& .MuiSelect-icon": {
    color: theme.colors.text[6],
    fontSize: 20,
    right: 18,
  },
});

interface LabeledSelectFieldOption<T extends string> {
  value: T;
  label: string;
}

interface LabeledSelectFieldProps<T extends string> {
  label: string;
  value: T;
  onChange: (value: T) => void;
  options: LabeledSelectFieldOption<T>[];
  selectWidth: number | string;
  containerWidth?: number;
  containerHeight?: number;
  menuMaxHeight?: number | string;
  menuMaxWidth?: number | string;
  loading?: boolean;
  labelAdornment?: ReactNode;
  fontSize?: number | string;
}

export function LabeledSelectField<T extends string>({
  label,
  value,
  onChange,
  options,
  selectWidth,
  containerWidth,
  containerHeight,
  menuMaxHeight = 400,
  menuMaxWidth = 350,
  loading = false,
  labelAdornment,
  fontSize = 14,
}: LabeledSelectFieldProps<T>): ReactElement {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: 0.5,
        ...(containerWidth ? { width: containerWidth } : {}),
        ...(containerHeight ? { height: containerHeight } : {}),
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Typography sx={getFigmaFieldLabelSx(theme)}>{label}</Typography>
        {labelAdornment}
      </Box>

      {loading ? (
        <Skeleton
          height={40}
          sx={{ borderRadius: "16px", transform: "none" }}
          width={selectWidth}
        />
      ) : (
        <Select
          IconComponent={KeyboardArrowDown}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: menuMaxHeight,
                maxWidth: menuMaxWidth,
                scrollBehavior: "auto",
              },
            },
          }}
          onChange={(e: SelectChangeEvent<T>) => onChange(e.target.value as T)}
          size="small"
          sx={getFigmaRoundedSelectSx(theme, selectWidth, fontSize)}
          value={value}
        >
          {options.map((option) => (
            <MenuItem
              key={option.value}
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                display: "block",
                fontSize,
              }}
              value={option.value}
            >
              {option.label}
            </MenuItem>
          ))}
        </Select>
      )}
    </Box>
  );
}
